/**
 * "Sign in with Face/Touch ID" button — shown on the login pages once we
 * know the device has a platform authenticator. Triggers a usernameless
 * passkey ceremony and, on success, redirects to the role-aware home page.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fingerprint } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  authenticateWithPasskey,
  passkeysSupported,
  platformAuthenticatorAvailable,
} from "@/lib/passkeys";

export function PasskeySignInButton({ defaultLandingPath }) {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!passkeysSupported()) return;
      const ok = await platformAuthenticatorAvailable();
      if (!cancelled) setAvailable(ok);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!available) return null;

  const onClick = async () => {
    setBusy(true);
    try {
      const user = await authenticateWithPasskey();
      toast.success(`Welcome back, ${(user?.name || "").split(" ")[0] || "team"}`);
      await refresh();
      const dest =
        defaultLandingPath ||
        (user?.role === "EMPLOYEE" ? "/clock" : "/admin");
      navigate(dest, { replace: true });
    } catch (err) {
      if (err?.cancelled) {
        // User dismissed the OS prompt — stay silent.
      } else {
        const detail = err?.response?.data?.detail || err?.message;
        if (detail) toast.error(detail, { duration: 7000 });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-3 text-xs text-gray-400">
        <div className="flex-1 border-t border-gray-200" />
        <span>or</span>
        <div className="flex-1 border-t border-gray-200" />
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={onClick}
        disabled={busy}
        className="w-full h-12 border-2 border-[#C9A84C]/40 bg-white hover:bg-[#C9A84C]/10 text-[#1A2744] font-semibold flex items-center justify-center gap-2"
        data-testid="passkey-signin-button"
      >
        <Fingerprint className="h-5 w-5 text-[#C9A84C]" />
        {busy ? "Waiting for device…" : "Sign in with Face / Touch ID"}
      </Button>
    </>
  );
}
